import { useBoardGroups, useBoardStatuses } from '@/features/boards/public';
import { useProjectMembers } from '@/features/projects/public';
import { useTags } from '@/features/tags/public';
import type { BoardContext } from '../types';
import type { TaskFieldOptions } from '../components/TaskFieldEditor';

export interface IdLabelOption {
  id: string;
  label: string;
  /** Palette token, for the kinds of value that carry a colour of their own: statuses and tags. */
  color?: string | null;
}

/** Narrowing helper: keeps rows whose id and name are both present. */
function withIdAndName<T extends { id: string | null; name: string | null }>(
  value: T,
): value is T & { id: string; name: string } {
  return value.id !== null && value.name !== null;
}

/**
 * Assembles the propose request's board context and the preview's option
 * lists from the sibling-feature hooks (boards/projects/tags — never tasks).
 */
export function useBoardContext(projectId: string, boardId: string) {
  const { statuses, isLoading: statusesLoading } = useBoardStatuses(boardId);
  const { groups, isLoading: groupsLoading } = useBoardGroups(boardId);
  const membersQuery = useProjectMembers(projectId);
  const tagsQuery = useTags(projectId);

  const members = membersQuery.data ?? [];
  const tags = tagsQuery.data?.items ?? [];

  // Both shapes come out of one pass per list. Filtering and mapping each source
  // twice — once for the request context, once for the preview's labels — walked
  // statuses and tags four times each to produce the same two rows.
  const statusContext: BoardContext['statuses'] = [];
  const statusOptions: IdLabelOption[] = [];
  for (const status of statuses) {
    if (!withIdAndName(status)) continue;
    statusContext.push({ id: status.id, name: status.name });
    statusOptions.push({ id: status.id, label: status.name, color: status.color });
  }

  const tagContext: BoardContext['tags'] = [];
  const tagOptions: IdLabelOption[] = [];
  for (const tag of tags) {
    if (!withIdAndName(tag)) continue;
    tagContext.push({ id: tag.id, name: tag.name });
    tagOptions.push({ id: tag.id, label: tag.name, color: tag.color });
  }

  const memberContext: BoardContext['members'] = [];
  const memberOptions: IdLabelOption[] = [];
  for (const member of members) {
    const name = member.displayName ?? member.email;
    memberContext.push({ id: member.userId, name, email: member.email });
    memberOptions.push({ id: member.userId, label: name });
  }

  const groupOptions: IdLabelOption[] = [];
  for (const group of groups) {
    if (withIdAndName(group)) {
      groupOptions.push({ id: group.id, label: group.name });
    }
  }

  const boardContext: BoardContext = {
    statuses: statusContext,
    groups: groups.map((group) => ({ id: group.id, name: group.name })),
    members: memberContext,
    tags: tagContext,
  };

  const options: TaskFieldOptions = {
    statuses: statusOptions,
    groups: groupOptions,
    members: memberOptions,
    tags: tagOptions,
  };

  const isLoading =
    statusesLoading || groupsLoading || membersQuery.isLoading || tagsQuery.isLoading;

  return { boardContext, options, isLoading };
}
